import * as React from 'react';
import { styled } from '@glitz/react';
import { monochromeDark } from 'svenssons/shared/theme';
import { translate, useDynamicData } from '@avensia/excite';
import { authenticateIfNeeded } from '@avensia/excite-commercetools/authentication';
import Image from 'webapp/components/image';
import { ResetButton } from 'webapp/components/button/button';
import Link from 'webapp/components/Link';
import { useViewEventTrigger } from 'webapp/tracking';
import { addSelectPromotionToDataLayer, addViewPromotionToDataLayer } from 'webapp/tracking/events/promotions';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type PropTypeProductDiscount = {
  productDiscount: NordicNest.Api.ProductDiscountApiModel;
};

export const DiscountInfo = (props: PropTypeProductDiscount) => {
  const isLoggedIn = useDynamicData<NordicNest.Api.CustomerApiModel>()[0]?.isLoggedIn;
  const self = React.useRef<HTMLDivElement>(null);

  useViewEventTrigger(self, addViewPromotionToDataLayer, {
    content: props.productDiscount,
  });

  return (
    <DiscountInfoWrapper
      css={props.productDiscount.image && withImageDecorator}
      ref={self}
      {...NnTestIdProps.PdpDiscountInfo}
    >
      <Image image={props.productDiscount.image} />
      <Content css={props.productDiscount.image && withImageContent}>
        <SemiBold>{props.productDiscount.header}</SemiBold>
        <Header>{props.productDiscount.title}</Header>
        <BottomRow>
          <div>
            <SemiBold>{props.productDiscount.discountInformation}</SemiBold>
            <SemiBold>{props.productDiscount.limitation}</SemiBold>
            <div>{props.productDiscount.dateLimitation}</div>
          </div>
          {props.productDiscount.isMcp && !isLoggedIn ? (
            <MemberButton onClick={() => authenticateIfNeeded()}>
              {translate(t => t.productPage.becomeMember)}
            </MemberButton>
          ) : (
            props.productDiscount.link && (
              <DiscountLink
                {...props.productDiscount.link}
                onClick={() => addSelectPromotionToDataLayer({ content: props.productDiscount })}
              />
            )
          )}
        </BottomRow>
      </Content>
    </DiscountInfoWrapper>
  );
};

const DiscountInfoWrapper = styled.div({
  display: 'grid',
  alignItems: 'center',
  border: { xy: { style: 'solid', color: monochromeDark, width: 1 } },
});

const withImageDecorator = styled({
  gridTemplateColumns: '122px auto',
});

const Content = styled.div({
  padding: { xy: t => t.sharedSpacing.m },
  fontSize: t => t.sharedFontSize.xs,
});

const withImageContent = styled({
  padding: { left: t => t.sharedSpacing.xs },
});

const SemiBold = styled.div({
  fontWeight: 400,
});

const Header = styled.h3({
  fontSize: t => t.sharedFontSize.l,
  fontWeight: 450,
  margin: { y: t => t.sharedSpacing.xxs },
});

const BottomRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  columnGap: t => t.sharedSpacing.s,
});

const MemberButton = styled(ResetButton, {
  textDecoration: 'underline',
});

const DiscountLink = styled(Link, {
  textDecoration: 'underline',
  ':hover': {
    textDecoration: 'underline',
  },
});
